import React, { useEffect, useState } from 'react';
import TrodoLoader from 'shared/components/ui/TrodoLoader';
import { DRAWER_BOUNDS_CLASS } from 'shared/components/ui/DrawerScrim';
import { JsonRawView } from 'features/agent-analytics/components/runCells';
import axiosInstance from 'shared/services/api/axiosInstance';

async function getUxSignalDetail(teamId, uxSignalId) {
  const { data } = await axiosInstance.get(
    `/events/event-ux-signals/${encodeURIComponent(uxSignalId)}`,
    { params: { team_id: teamId } },
  );
  return data;
}

/** Omit null, undefined, blank strings, empty arrays, empty objects. Keep 0 and false. */
function isMeaningfulValue(val) {
  if (val == null) return false;
  if (typeof val === 'string') return val.trim() !== '';
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === 'object') return Object.keys(val).length > 0;
  return true;
}

function sortedMeaningfulEntries(obj) {
  if (!obj || typeof obj !== 'object') return [];
  return Object.keys(obj)
    .sort()
    .map((k) => [k, obj[k]])
    .filter(([, val]) => isMeaningfulValue(val));
}

function FieldGrid({ title, data }) {
  if (!data || typeof data !== 'object') return null;
  const rows = sortedMeaningfulEntries(data);
  if (rows.length === 0) return null;
  return (
    <section className="rounded-lg border border-[color:var(--border-default)] bg-surface p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">{title}</p>
      <dl className="mt-2 space-y-2 text-[12px]">
        {rows.map(([key, val]) => (
          <div key={key} className="min-w-0 grid gap-1 sm:grid-cols-[minmax(0,140px)_1fr]">
            <dt className="shrink-0 font-mono text-[11px] text-[color:var(--text-muted)]">{key}</dt>
            <dd className="min-w-0 break-words text-[color:var(--text-primary)]">
              {val != null && typeof val === 'object' ? (
                <JsonRawView value={val} />
              ) : key === 'current_url' ? (
                <span className="break-all font-mono text-[11px]">{String(val)}</span>
              ) : (
                String(val)
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * Full-screen-adjacent drawer: event_ux_signals row + parent events row when linked.
 */
export default function UxSignalDetailDrawer({ teamId, uxSignalId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!teamId || !uxSignalId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getUxSignalDetail(teamId, uxSignalId)
      .then((data) => {
        if (!cancelled) setPayload(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || err?.message || 'Failed to load.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [teamId, uxSignalId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const ux = payload?.ux_signal;
  const ev = payload?.event;

  return (
    // Beside the list, inside the app's content panel — see DrawerScrim. Esc
    // and the close control are how it closes; nothing behind it is dimmed.
    <div className={`${DRAWER_BOUNDS_CLASS} z-[60]`} role="region" aria-label="UX signal detail">
      <aside
        className="pointer-events-auto flex h-full w-full max-w-[560px] flex-col overflow-hidden border-l border-t border-[color:var(--border-default)] bg-surface shadow-2xl"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <header className="flex items-center justify-between border-b border-[color:var(--border-default)] bg-surface px-4 py-3">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-[color:var(--text-muted)]">UX signal</p>
            <p className="truncate font-mono text-[13px] font-semibold text-[color:var(--text-primary)]">
              {ux?.signal_type || '—'} · #{uxSignalId}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-[12px] text-[color:var(--text-secondary)] hover:bg-[color:var(--surface-sunken)]"
          >
            Close ✕
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center py-12"><TrodoLoader label="Loading signal" /></div>
          ) : error ? (
            <p className="text-[12px] text-[color:var(--feedback-error)]">{error}</p>
          ) : (
            <div className="space-y-3">
              <FieldGrid title="Signal properties" data={ux} />
              {ev ? <FieldGrid title="Parent event" data={ev} /> : (
                <p className="text-[11px] text-[color:var(--text-muted)]">No linked parent event row.</p>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
